// Status types for Scout PWA status grid components
// Mirrors visitor-pwa types for consistent status reporting

export type StatusCategory = 'CONSUMABLE' | 'EQUIPMENT' | 'CLEANLINESS' | 'COMFORT';

export interface StatusItem {
  id: string;
  statusType: {
    id: string;
    name: string;
    category: StatusCategory;
    icon: string | null;
  };
  currentStatus: string;
  lastUpdatedAt: string;
}

export const STATUS_COLORS: Record<string, string> = {
  OK: 'bg-forest-wash text-forest border-forest/40',
  WORKING: 'bg-forest-wash text-forest border-forest/40',
  CLEAN: 'bg-forest-wash text-forest border-forest/40',
  COMFORTABLE: 'bg-forest-wash text-forest border-forest/40',
  LOW: 'bg-gold/10 text-gold border-gold/40',
  NEEDS_ATTENTION: 'bg-gold/10 text-gold border-gold/40',
  EMPTY: 'bg-critical/10 text-critical border-critical/40',
  ISSUE: 'bg-critical/10 text-critical border-critical/40',
  NOT_WORKING: 'bg-critical/10 text-critical border-critical/40',
  DIRTY: 'bg-critical/10 text-critical border-critical/40',
  UNCOMFORTABLE: 'bg-critical/10 text-critical border-critical/40',
};

export const DEFAULT_STATUS_COLOR = 'bg-primary/5 text-primary border-primary/30';

export const STATUS_ICONS: Record<string, string> = {
  OK: '\u2713',
  WORKING: '\u2713',
  CLEAN: '\u2713',
  COMFORTABLE: '\u2713',
  LOW: '\u26A0',
  NEEDS_ATTENTION: '\u26A0',
  EMPTY: '\u2717',
  ISSUE: '\u2717',
  NOT_WORKING: '\u2717',
  DIRTY: '\u2717',
  UNCOMFORTABLE: '\u2717',
};

export const CATEGORY_KEYS: Record<StatusCategory, string> = {
  CONSUMABLE: 'consumable',
  EQUIPMENT: 'equipment',
  CLEANLINESS: 'cleanliness',
  COMFORT: 'comfort',
};

export const CATEGORY_ORDER: StatusCategory[] = [
  'CONSUMABLE',
  'EQUIPMENT',
  'CLEANLINESS',
  'COMFORT',
];

export interface StatusOption {
  value: string;
  labelKey: string;
  descriptionKey: string;
  icon: string;
}

export const STATUS_OPTIONS: Record<StatusCategory, StatusOption[]> = {
  CONSUMABLE: [
    { value: 'OK', labelKey: 'ok', descriptionKey: 'ok', icon: '\u2713' },
    { value: 'LOW', labelKey: 'low', descriptionKey: 'low', icon: '\u26A0' },
    { value: 'EMPTY', labelKey: 'empty', descriptionKey: 'empty', icon: '\u2717' },
  ],
  EQUIPMENT: [
    { value: 'WORKING', labelKey: 'working', descriptionKey: 'working', icon: '\u2713' },
    { value: 'ISSUE', labelKey: 'issue', descriptionKey: 'issue', icon: '\u26A0' },
    { value: 'NOT_WORKING', labelKey: 'notWorking', descriptionKey: 'notWorking', icon: '\u2717' },
  ],
  CLEANLINESS: [
    { value: 'CLEAN', labelKey: 'clean', descriptionKey: 'clean', icon: '\u2713' },
    { value: 'NEEDS_ATTENTION', labelKey: 'needsAttention', descriptionKey: 'needsAttention', icon: '\u26A0' },
    { value: 'DIRTY', labelKey: 'dirty', descriptionKey: 'dirty', icon: '\u2717' },
  ],
  COMFORT: [
    { value: 'COMFORTABLE', labelKey: 'comfortable', descriptionKey: 'comfortable', icon: '\u2713' },
    { value: 'UNCOMFORTABLE', labelKey: 'uncomfortable', descriptionKey: 'uncomfortable', icon: '\u2717' },
  ],
};

export const BAD_STATUSES = new Set([
  'LOW',
  'EMPTY',
  'ISSUE',
  'NOT_WORKING',
  'NEEDS_ATTENTION',
  'DIRTY',
  'UNCOMFORTABLE',
]);

export function formatStatus(status: string): string {
  return status.replace(/_/g, ' ').toLowerCase();
}

export function getStatusColor(status: string): string {
  return STATUS_COLORS[status] || DEFAULT_STATUS_COLOR;
}

export function getStatusIcon(status: string): string {
  return STATUS_ICONS[status] || '?';
}
